import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal } from 'react-bootstrap';
import { Stage, Layer, Rect, Text, Group, Circle } from 'react-konva';
import toast from 'react-hot-toast';
import { getAllEvents } from '../../services/eventService';
import { createBooking } from '../../services/bookingService';
import { getSeatingPlanById } from '../../services/eventService';


const AdminBooking = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [seatingPlan, setSeatingPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: ''
  });
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  // Enhanced email validation function
  const validateEmail = (email) => {
    // Basic structure check
    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!basicEmailRegex.test(email)) {
      return false;
    }
    
    // Additional checks for common malformed patterns
    const malformedPatterns = [
      /\.{2,}/,           // Multiple consecutive dots
      /@.*@/,             // Multiple @ symbols
      /^\./,              // Starts with dot
      /\.$/,              // Ends with dot
      /@\./,              // @ followed by dot
      /\.@/,              // Dot followed by @
      /[^\w\.@-]/,        // Invalid characters (only allow word chars, dots, @, hyphens)
      /\.{1,}@/,          // Dots before @
      /@\.{1,}/,          // Dots after @
      /^[^a-zA-Z0-9]/,    // Doesn't start with alphanumeric
      /[^a-zA-Z0-9]$/,    // Doesn't end with alphanumeric
    ];
    
    // Check for malformed patterns
    for (const pattern of malformedPatterns) {
      if (pattern.test(email)) {
        return false;
      }
    }
    
    // Split email into local and domain parts
    const [localPart, domainPart] = email.split('@');
    
    // Validate local part (before @)
    if (localPart.length === 0 || localPart.length > 64) {
      return false;
    }
    
    // Validate domain part (after @)
    if (domainPart.length === 0 || domainPart.length > 253) {
      return false;
    }
    
    // Check domain has at least one dot and valid TLD
    const domainParts = domainPart.split('.');
    if (domainParts.length < 2) {
      return false;
    }
    
    // Check TLD (last part) is at least 2 characters and only letters
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
      return false;
    }
    
    // Check each domain part is not empty and doesn't start/end with hyphen
    for (const part of domainParts) {
      if (part.length === 0 || part.startsWith('-') || part.endsWith('-')) {
        return false;
      }
    }
    
    return true;
  };

  // Handle email input change with validation
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setBookingData({...bookingData, customerEmail: email});
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await getAllEvents();
      const allEvents = response.data?.data || [];
      
      // Filter events to show only future events
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Set to start of current day
      
      const futureEvents = allEvents.filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        return eventDate >= currentDate;
      });
      
      setEvents(futureEvents);
    } catch (err) {
      toast.error('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSeats = (seatingPlanData) => {
    
    // Handle the array structure - take the first item
    const plan = Array.isArray(seatingPlanData) ? seatingPlanData[0] : seatingPlanData;
    
    if (!plan || !plan.sections) {
      console.log('No seating plan data:', seatingPlanData);
      return [];
    }
    
    const seats = [];
    const seatWidth = 25;
    const seatHeight = 25;
    const gap = 5;
    const sectionsPerRow = 5; // Show 5 sections per row
    let currentY = gap;

    plan.sections.forEach((section, sectionIndex) => {
      const rowIndex = Math.floor(sectionIndex / sectionsPerRow);
      const colIndex = sectionIndex % sectionsPerRow;
      const x = gap + (colIndex * 200); // 200px width per section group
      const y = currentY + (rowIndex * 150); // 150px height per section group
      
      section.rows.forEach((row, rowIndex) => {
        row.seats.forEach((seat, seatIndex) => {

          seats.push({
            x: x + (seatIndex * (seatWidth + gap)),
            y: y + (rowIndex * (seatHeight + gap)),
            row: row.rowNumber,
            section: section.name,
            // seatNumber: seat.seatNumber,
            
            //  seatNumber: seat.seatNumber ?? seatIndex  + 1,
          
            seatId: seat._id,
            price: seat.price,
            fill: seat.reserved ? '#ccc' : seat.color,
            category: seat.level,
            reserved: seat.reserved,
            originalColor: seat.color,
            

          });
          
        });

      });
        

      // Update currentY for the next row of sections
      if (colIndex === sectionsPerRow - 1) {
        currentY += 150;
      }
    });
    
    console.log('Generated seats:', seats.length);
    return seats;
  };

  const getSeatColor = (section, rowOverride, categories) => {
    const category = rowOverride !== 'DEFAULT' ? rowOverride : categories[section].category;
    switch(category) {
      case 'VIP':
        return '#FFD700'; // Gold for VIP
      case 'PREMIUM':
        return '#DA70D6'; // Orchid for Premium
      default:
        return 'lightblue'; // Standard
    }
  };

  const handleSeatClick = (seat) => {
     console.log('CLICKED SEAT:', seat.seatNumber); 
     
    // Check if this is a space seat (empty level and price)
    if (!seat.level && seat.price === 0) {
      toast.error('This is an aisle space and cannot be booked');
      return;
    }
    
    if (seat.reserved) {
      toast.error('This seat is already reserved');
      return;
    }
    setSelectedSeats(prev => {
      const isSelected = prev.some(s => s.seatId === seat.seatId);
      if (isSelected) {
        return prev.filter(s => s.seatId !== seat.seatId);
      } else {
        return [...prev, seat];
      }
    });
  };

  const handleEventSelect = async (event) => {
    setSelectedEvent(event);
    setSelectedSeats([]);
    setSeatingPlan( );
    
    if (event && event.seatingPlan) {
      try {
        setLoading(true);
        const seatingPlanData = await getSeatingPlanById(event.seatingPlan);
        console.log('Seating plan data received:', seatingPlanData);
        setSeatingPlan(seatingPlanData);
      } catch (err) {
        toast.error('Failed to fetch seating plan');
        console.error('Error fetching seating plan:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBookingSubmit = async () => {
    if (selectedSeats.length === 0) {
      toast.error('Please select at least one seat');
      return;
    }

    if (!bookingData.customerName || !bookingData.customerEmail) {
      toast.error('Please fill in customer details');
      return;
    }

    if (!validateEmail(bookingData.customerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);

      const bookingPayload = {
        seats: selectedSeats.map((seat, index) => ({
          seatId: seat.seatId,
          section: seat.section,
          row: seat.row,
          // seatNumber: seat.seatNumber,
          seatNumber: seat.seatNumber,
          price: seat.price,
          category: seat.category
        })),
        eventId: selectedEvent._id,
        seatId: selectedEvent.seatingPlan, // Add seatmap ID
        customerName: bookingData.customerName,
        customerEmail: bookingData.customerEmail,
        customerPhone: bookingData.customerPhone
      };

      await createBooking(bookingPayload);
      toast.success('Booking created successfully!');
      setSelectedSeats([]);
      setBookingData({
        customerName: '',
        customerEmail: '',
        customerPhone: ''
      });
      
      setShowBookingModal(false);
    } catch (err) {
      toast.error('Failed to create booking. Please try again.');
      console.error('Booking error:', err);
    } finally {
      setLoading(false);
      
      // Refresh the seating layout to show updated seat status
      if (selectedEvent && selectedEvent.seatingPlan) {
        // Use setTimeout to ensure this runs after the loading state is cleared
        setTimeout(async () => {
          try {
            console.log('Refreshing seating plan after booking...');
            const seatingPlanData = await getSeatingPlanById(selectedEvent.seatingPlan);
            console.log('New seating plan data:', seatingPlanData);
            setSeatingPlan(seatingPlanData);
          } catch (err) {
            console.error('Error refreshing seating plan:', err);
          }
        }, 100);
      }
    }
  };
   const renderRoundTable = ({

  centerX = 0,
  centerY = 0,
  seats = [],
  selectedSeats,
  handleSeatClick,
  elements,
  sectionName,
  rowNumber,
  sectionIndex,
  
  rowIndex
  
}) => {
  const tableRadius = 35;
  const seatRadius = 12;
  const seatDistance = 55;

  // Draw table circle
  elements.push(
    <Circle
      key={`table-${sectionIndex}-${rowIndex}`}
      x={centerX}
      y={centerY}
      radius={tableRadius}
      fill="#795548"
    />
  );

  // Optional table label
  elements.push(
    <Text
      key={`table-label-${sectionIndex}-${rowIndex}`}
      x={centerX - 20}
      y={centerY - 8}
      width={40}
      text={`${sectionName}`}
      fontSize={12}
      fill="#fff"
      align="center"
    />
  );

  // Draw seats around table
  const totalSeats = seats.length;
  const angleStep = (2 * Math.PI) / totalSeats;

  seats.forEach((seat, seatIndex) => {
    const angle = seatIndex * angleStep;
    const seatX = centerX + Math.cos(angle) * seatDistance;
    const seatY = centerY + Math.sin(angle) * seatDistance;

    const isSelected = selectedSeats.some(s => s.seatId === seat._id);

    elements.push(
      <Group
        key={`round-seat-${sectionIndex}-${rowIndex}-${seatIndex}`}
        onClick={() => handleSeatClick({
          ...seat,
          section: sectionName,
          row: rowNumber,
          seatNumber: seat.seatNumber ?? seatIndex + 1,
          seatId: seat._id
        })}
      >
        <Circle
          x={seatX}
          y={seatY}
          radius={seatRadius}
          fill={isSelected ? '#1976d2' : seat.reserved ? '#e74c3c' : '#43a047'}
          stroke="#222"
        />
        <Text
          x={seatX - 8}
          y={seatY - 6}
          width={16}
          
          text={`${seat.seatNumber ?? seatIndex + 1}`}
          // text={String(seat.seatNumber)}

          fontSize={10}
          fill="#fff"
          align="center"
        />
      </Group>
    );
  });
};
const renderRectangleTable = ({
  x = 0,
  y = 0,
  seats = [],
  selectedSeats,
  handleSeatClick,
  elements,
  sectionName,
  sectionIndex,
  rowIndex
}) => {
  const tableWidth = 140;
  const tableHeight = 50;
  const seatSize = 22;
  const seatGap = 6;

  // Draw rectangle table
  elements.push(
    <Rect
      key={`rect-table-${sectionIndex}-${rowIndex}`}
      x={x}
      y={y}
      width={tableWidth}
      height={tableHeight}
      fill="#795548"
      cornerRadius={6}
    />
  );

  // Table label
  elements.push(
    <Text
      key={`rect-label-${sectionIndex}-${rowIndex}`}
      x={x}
      y={y + 16}
      width={tableWidth}
      text={sectionName}
      fontSize={12}
      fill="#fff"
      align="center"
    />
  );

  // Draw seats (top & bottom)
  seats.forEach((seat, seatIndex) => {
    const isTop = seatIndex % 2 === 0;
    const index = Math.floor(seatIndex / 2);

    const seatX =
      x + 10 + index * (seatSize + seatGap);

    const seatY =
      isTop
        ? y - seatSize - 6
        : y + tableHeight + 6;

    const isSelected = selectedSeats.some(
      s => s.seatId === seat._id
    );

    elements.push(
      <Group
        key={`rect-seat-${sectionIndex}-${rowIndex}-${seatIndex}`}
        onClick={() =>
          handleSeatClick({
            ...seat,
            section: sectionName,
            seatId: seat._id
          })
        }
      >
        <Rect
          x={seatX}
          y={seatY}
          width={seatSize}
          height={seatSize}
          fill={
            isSelected
              ? '#1976d2'
              : seat.reserved
              ? '#e74c3c'
              : '#43a047'
          }
          stroke="#222"
          cornerRadius={4}
        />
        <Text
          x={seatX}
          y={seatY + 4}
          width={seatSize}
          // text={`${seat.seatNumber ?? seatIndex + 1}`}
          text={String(seat.seatNumber)}

          fontSize={10}
          fill="#fff"
          align="center"
        />
      </Group>
    );
  });
};


  const generateSeatMapElements = (seatingPlanData, selectedSeats, handleSeatClick) => {
 const TABLE_RADIUS = 35;   // matches renderRoundTable
const SEAT_DISTANCE = 55;  // matches renderRoundTable
const RECT_TABLE_HEIGHT = 50; // matches renderRectangleTable
const RECT_TABLE_SPACING = 20; // spacing between rectangle tables


    // Handle the array structure - take the first item
    const plan = Array.isArray(seatingPlanData) ? seatingPlanData[0] : seatingPlanData;
    if (!plan || !plan.sections) return { elements: [], width: 800, height: 600 };
 plan.sections.forEach(section => {
  section.rows.forEach(row => {
    row.seats = row.seats.map((seat, index) => ({
      ...seat,
      seatNumber:
        seat.seatNumber !== null && seat.seatNumber !== undefined
          ? seat.seatNumber
          : index + 1
    }));
  });
});
console.log(
  'NORMALIZED:',
  plan.sections[0].rows[0].seats.map(s => s.seatNumber)
);
    const seatWidth = 28;
    const seatHeight = 28;
    const seatGap = 8;
    const rowGap = 10;
    const sectionGap = 30;
    const sectionLabelWidth = 90;
    const rowLabelWidth = 0; // Not used, but can be added for row numbers
    let y = 40; // Start below the stage
    let maxRowSeats = 0;
    let elements = [];

    // Draw stage
    elements.push(
      <Rect key="stage-bg" x={sectionLabelWidth} y={0} width={700} height={28} fill="#666" cornerRadius={4} />
    );
    elements.push(
      <Text key="stage-label" x={sectionLabelWidth + 300} y={5} text="STAGE" fontSize={18} fill="#fff" width={120} align="center" />
    );

    plan.sections.forEach((section, sectionIndex) => {
      const isTableLayout = ['round', 'rectangular'].includes(
  section.tableType?.toLowerCase()
);
      // Section label
      {!isTableLayout && (
      elements.push(
        
        <Text
          key={`section-label-${sectionIndex}`}
          x={0}
          y={y + 5}
          // text={section.name}
          fontSize={14}
          fill="#444"
          width={sectionLabelWidth - 10}
          align="right"
        />
      )
    )}
      let sectionStartY = y;
     section.rows.forEach((row, rowIndex) => {

  // if (row.type?.toLowerCase() === 'round') {
  //   const centerX = sectionLabelWidth + Math.max(row.seats.length * (seatWidth + seatGap), 140) / 2;
  //   const centerY = y + 60;

  //   renderRoundTable({
  //     centerX,
  //     centerY,
  //     seats: row.seats,
  //     selectedSeats,
  //     handleSeatClick,
  //     elements,
  //     sectionName: section.name,
  //     rowNumber: row.rowNumber,
  //     sectionIndex,
  //     rowIndex
  //   });

  //   y += 2 * 35 + 55 + 20; // table radius + seatDistance + buffer

  // }
  
  //  else if (row.seats && row.seats.length > 0) {

  //   row.seats.forEach((seat, seatIndex) => {
  //     const isSpace =
  //       (!seat.level || seat.level === '') &&
  //       (seat.price === 0 || seat.price === null) &&
  //       (seat.seatNumber === null || seat.seatNumber === undefined);

  //     const seatX = sectionLabelWidth + seatIndex * (seatWidth + seatGap);
  //     const seatY = y;

  //     if (isSpace) {
  //       elements.push(
  //         <Rect
  //           key={`space-${sectionIndex}-${rowIndex}-${seatIndex}`}
  //           x={seatX}
  //           y={seatY}
  //           width={seatWidth}
  //           height={seatHeight}
  //           fill="transparent"
  //           stroke="#ddd"
  //           strokeWidth={1}
  //           strokeDasharray={[3, 3]}
  //           opacity={0.5}
  //         />
  //       );
  //     } else {
  //       const seatObj = { ...seat, seatId: seat._id, section: section.name, row: row.rowNumber };
  //       const isSelected = selectedSeats.some(s => s.seatId === seat._id);

  //       elements.push(
  //         <Group key={`seat-${sectionIndex}-${rowIndex}-${seatIndex}`} onClick={() => handleSeatClick(seatObj)}>
  //           <Rect
  //             x={seatX}
  //             y={seatY}
  //             width={seatWidth}
  //             height={seatHeight}
  //             fill={isSelected ? '#1976d2' : seat.reserved ? '#e74c3c' : '#43a047'}
  //             stroke={isSelected ? '#fff' : seat.reserved ? '#c0392b' : '#222'}
  //             strokeWidth={isSelected ? 2 : 1}
  //             cornerRadius={6}
  //             opacity={seat.reserved ? 0.8 : 1}
  //           />
  //           <Text
  //             x={seatX}
  //             y={seatY + 7}
  //             width={seatWidth}
  //             // text={`${seat.seatNumber ?? ''}`}
  //             text={`${seat.seatNumber ?? seatIndex + 1}`} 
  //             fontSize={12}
  //             fill={isSelected ? '#fff' : seat.reserved ? '#fff' : '#222'}
  //             align="center"
  //           />
  //         </Group>
  //       );
  //     }
  //   });

  //   maxRowSeats = Math.max(maxRowSeats, row.seats.length);
  //   y += seatHeight + rowGap;

  // }
const tableType = section.tableType?.toLowerCase();
const centerX = row.x;
const centerY = row.y;
if (tableType === 'round') {
  const centerX = sectionLabelWidth + Math.max(row.seats.length * (seatWidth + seatGap), 140) / 2;
  const centerY = y + TABLE_RADIUS + 10; // 10px buffer

  renderRoundTable({
    centerX: section.x,
    centerY: section.y,
    seats: row.seats,
    selectedSeats,
    handleSeatClick,
    elements,
    sectionName: section.name,
    rowNumber: row.rowNumber,
    sectionIndex,
    rowIndex
  });
// y += 2 * TABLE_RADIUS + SEAT_DISTANCE + 20;

}
else if (tableType === 'rectangular') {
  renderRectangleTable({
   x: row.x,
  y: row.y,
    seats: row.seats,
    selectedSeats,
    handleSeatClick,
    elements,
    sectionName: section.name,
    sectionIndex,
    rowIndex
  });

 // rectangle table height + spacing
}
else {
  // NORMAL seat rows only
  row.seats.forEach((seat, seatIndex) => {
    const seatX = sectionLabelWidth + seatIndex * (seatWidth + seatGap);
    const seatY = y;
 const isSelected = selectedSeats.some(s => s.seatId === seat._id);

    elements.push(
      <Group key={`seat-${sectionIndex}-${rowIndex}-${seatIndex}`} 
          onClick={() => handleSeatClick({
  ...seat,
  section: section.name,
  row: row.rowNumber ?? rowIndex + 1,
  seatNumber: seat.seatNumber ?? seatIndex + 1
  // seatNumber: seat.seatNumber ?? seatIndex + 1
})}
>
        <Rect
          x={seatX}
          y={seatY}
          width={seatWidth}
          height={seatHeight}
          fill={isSelected ? '#1976d2' : seat.reserved ? '#e74c3c' : seat.color ?? '#43a047'}
          stroke="#222"
          cornerRadius={4}
        />
        <Text
          x={seatX}
          y={seatY + 6}
          width={seatWidth}
          text={`${seat.seatNumber ?? seatIndex + 1}`} // fallback number
          fontSize={10}
          fill="#fff"
          align="center"
        />
      </Group>
    );
    // existing seat rendering (unchanged)
  });

  maxRowSeats = Math.max(maxRowSeats, row.seats.length);
  y += seatHeight + rowGap;
}

});

      y += sectionGap;
    });
    // Calculate canvas size
    // const width = sectionLabelWidth + maxRowSeats * (seatWidth + seatGap) + 40;
    // const height = y + 40;
 
// const width = sectionLabelWidth + maxRowSeats * (seatWidth + seatGap) + 40;
const width= 1600;
// const height = y + 40; // 40px padding at bottom
const height = 1600;
    return { elements, width, height };
  };

  const seatMap = seatingPlan ? generateSeatMapElements(seatingPlan, selectedSeats, handleSeatClick) : { elements: [], width: 800, height: 600 };

  return (
    <Container className="mt-4">
      <h1 className="mb-4">Admin Booking</h1>

      <Row>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5>Select Event</h5>
              <small className="text-muted">Showing upcoming events only</small>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <p>Loading events...</p>
              ) : events.length === 0 ? (
                <div className="text-center text-muted">
                  <p>No upcoming events available</p>
                  <small>Only future events can be booked</small>
                </div>
              ) : (
                <Form.Select 
                  value={selectedEvent?._id || ''} 
                  onChange={(e) => {
                    const event = events.find(ev => ev._id === e.target.value);
                    handleEventSelect(event);
                  }}
                >
                  <option value="">Choose an event...</option>
                  {events.map(event => (
                    <option key={event._id} value={event._id}>
                      {event.title} - {new Date(event.date).toLocaleDateString('en-GB', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Card.Body>
          </Card>

          {selectedEvent && (
            <Card className="mt-3">
              <Card.Header>
                <h5>Event Details</h5>
              </Card.Header>
              <Card.Body>
                <p><strong>Title:</strong> {selectedEvent.title}</p>
                <p><strong>Date:</strong> {new Date(selectedEvent.date).toLocaleDateString()}</p>
                <p><strong>Venue:</strong> {selectedEvent.venue}</p>
              </Card.Body>
            </Card>
          )}

          {selectedSeats.length > 0 && (
            <Card className="mt-3">
              <Card.Header>
                <h5>Selected Seats ({selectedSeats.length})</h5>
              </Card.Header>
              <Card.Body>
                {selectedSeats.map(seat => (
                  <div key={seat.seatId} className="d-flex justify-content-between mb-2">
                    <span>{seat.section} - Seat {seat.seatNumber}</span>
                    {/* <span>₹{seat.price}</span> */}
                  </div>
                ))}
                <hr />
                {/* <div className="d-flex justify-content-between">
                  <strong>Total:</strong>
                  <strong>₹{selectedSeats.reduce((sum, seat) => sum + seat.price, 0)}</strong>
                </div> */}
                <Button 
                  variant="primary" 
                  className="w-100 mt-3"
                  onClick={() => setShowBookingModal(true)}
                >
                  Book Seats
                </Button>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={8}>
          <Card>
            <Card.Header>
              <h5>Seat Map</h5>
            </Card.Header>
            <Card.Body>
              {selectedEvent ? (
                <div style={{ overflow: 'auto', maxHeight: '600px', width: '100%',background: '#fafbfc', borderRadius: 8, border: '1px solid #eee' }}>
                  {loading ? (
                    <div className="text-center py-4">
                      <p>Loading seat map...</p>
                    </div>
                  ) : seatingPlan ? (
                    <>
                      <Stage width={seatMap.width} height={seatMap.height}>
                        <Layer>
                          {seatMap.elements}
                        </Layer>
                      </Stage>
                      {/* Legend */}
                      <div style={{ display: 'flex', gap: 24, marginTop: 11, marginLeft: 24, position: 'absolute', top: 0, right: '2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 18, height: 18, background: '#43a047', borderRadius: 4, border: '1px solid #222' }}></div> <span style={{ fontSize: 13, color: '#222' }}>Available</span></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 18, height: 18, background: '#1976d2', borderRadius: 4, border: '1px solid #222' }}></div> <span style={{ fontSize: 13, color: '#222' }}>Selected</span></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 18, height: 18, background: '#e74c3c', borderRadius: 4, border: '1px solid #c0392b' }}></div> <span style={{ fontSize: 13, color: '#222' }}>Booked</span></div>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-muted">No seating plan available for this event</p>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted">Please select an event to view the seat map</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Booking Modal */}
      <Modal show={showBookingModal} onHide={() => {
    setShowBookingModal(false);
    setEmailError('');
  }}>
        <Modal.Header closeButton>
          <Modal.Title>Customer Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Customer Name *</Form.Label>
              <Form.Control
                type="text"
                value={bookingData.customerName}
                onChange={(e) => setBookingData({...bookingData, customerName: e.target.value})}
                placeholder="Enter customer name"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Customer Email *</Form.Label>
              <Form.Control
                type="email"
                value={bookingData.customerEmail}
                onChange={handleEmailChange}
                placeholder="Enter customer email"
                required
                isInvalid={!!emailError}
              />
              <Form.Control.Feedback type="invalid">
                {emailError}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Customer Phone</Form.Label>
              <Form.Control
                type="tel"
                value={bookingData.customerPhone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers, spaces, hyphens, parentheses, and plus sign
                  if (/^[\d\s\-\(\)\+]*$/.test(value)) {
                    setBookingData({...bookingData, customerPhone: value});
                  }
                }}
                placeholder="Enter customer phone (optional)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBookingModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBookingSubmit}
            disabled={loading || !!emailError || !bookingData.customerEmail}
          >
            {loading ? 'Creating Booking...' : 'Confirm Booking'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminBooking; 